import Close from '@mui/icons-material/Close'
import Visibility from '@mui/icons-material/Visibility'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { getSupportOohScreens, saveSupportOohConfig } from 'src/supportPortal/services/oohManagementApi'

const SUPPORTED_LOCAL_LANGUAGES = [
  { code: 'hi', label: 'Hindi' },
  { code: 'kn', label: 'Kannada' },
  { code: 'te', label: 'Telugu' },
]

// One editable practitioner OOH screen row.
function OohScreenRow({ screen, clinicLocationId, offlineEvents, onPreview }) {
  const cfg = screen?.config || {}
  const [saving, setSaving] = useState(false)
  const [hideMentorDetails, setHideMentorDetails] = useState(Boolean(cfg.hideMentorDetails))
  const [displayLanguage, setDisplayLanguage] = useState(cfg.displayLanguage || '')
  const [announcementsEnabled, setAnnouncementsEnabled] = useState(cfg.announcementsEnabled !== false)
  const [walkinEnabled, setWalkinEnabled] = useState(Boolean(cfg.walkinBookingEnabled))
  const [defaultEventId, setDefaultEventId] = useState(cfg.walkinDefaultEventId || '')
  const [specialEventIds, setSpecialEventIds] = useState(
    Array.isArray(cfg.walkinSpecialEventIds) ? cfg.walkinSpecialEventIds : [],
  )
  const [qrAlwaysVisible, setQrAlwaysVisible] = useState(Boolean(cfg.walkinQrAlwaysVisible))

  async function handleSave() {
    setSaving(true)
    try {
      const resp = await saveSupportOohConfig({
        mentorId: screen.mentorId,
        clinicLocationId,
        hideMentorDetails,
        displayLanguage: displayLanguage || '',
        announcementsEnabled,
        walkinBookingEnabled: walkinEnabled,
        walkinDefaultEventId: walkinEnabled ? defaultEventId : '',
        walkinSpecialEventIds: walkinEnabled
          ? specialEventIds.filter((id) => id !== defaultEventId)
          : [],
        walkinQrAlwaysVisible: walkinEnabled ? qrAlwaysVisible : false,
      })
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        toast.success('OOH screen settings saved')
      } else {
        toast.error('Could not save. Please retry.')
      }
    } catch (e) {
      console.error('save ooh config failed', e)
      toast.error('Could not save. Please retry.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Accordion disableGutters sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', pr: 1 }}>
          <Typography sx={{ fontWeight: 600, flex: 1 }}>
            {screen?.mentorName || screen?.mentorId}
          </Typography>
          {screen?.hasConfig ? (
            <Chip
              size="small"
              color={cfg.walkinBookingEnabled ? 'success' : 'default'}
              label={cfg.walkinBookingEnabled ? 'Walk-in QR on' : 'Configured'}
            />
          ) : (
            <Chip size="small" variant="outlined" label="Not configured" />
          )}
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={(e) => {
              e.stopPropagation()
              onPreview(screen.mentorId)
            }}
          >
            Preview
          </Button>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Switch checked={hideMentorDetails} onChange={(e) => setHideMentorDetails(e.target.checked)} />
            }
            label="Hide practitioner photo/details on screen"
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Local language (shown alongside English)
            </Typography>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={displayLanguage || ''}
              onChange={(e) => setDisplayLanguage(e.target.value)}
            >
              <MenuItem value="">
                <em>English only</em>
              </MenuItem>
              {SUPPORTED_LOCAL_LANGUAGES.map((l) => (
                <MenuItem key={l.code} value={l.code}>
                  {l.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={announcementsEnabled}
                onChange={(e) => setAnnouncementsEnabled(e.target.checked)}
              />
            }
            label="Audio announcements (call patient by token)"
          />
          <FormControlLabel
            control={<Switch checked={walkinEnabled} onChange={(e) => setWalkinEnabled(e.target.checked)} />}
            label="Show a booking QR on this screen"
          />
          {walkinEnabled && (
            <Box sx={{ pl: 1 }}>
              {offlineEvents.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No bookable in-person sessions found for this practitioner.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                    Default session (the QR books this)
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    displayEmpty
                    value={defaultEventId || ''}
                    onChange={(e) => setDefaultEventId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select a session</em>
                    </MenuItem>
                    {offlineEvents.map((ev) => (
                      <MenuItem key={ev?.uid} value={ev?.uid}>
                        {ev?.title}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="body2" sx={{ mt: 1.5, mb: 0.5 }}>
                    Special sessions the patient may also pick (optional)
                  </Typography>
                  {offlineEvents
                    .filter((ev) => ev?.uid !== defaultEventId)
                    .map((ev) => (
                      <FormControlLabel
                        key={ev?.uid}
                        control={
                          <Checkbox
                            checked={specialEventIds.includes(ev?.uid)}
                            onChange={(e) =>
                              setSpecialEventIds((prev) =>
                                e.target.checked
                                  ? [...prev, ev.uid]
                                  : prev.filter((id) => id !== ev.uid),
                              )
                            }
                          />
                        }
                        label={ev?.title}
                      />
                    ))}
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Switch
                        checked={qrAlwaysVisible}
                        onChange={(e) => setQrAlwaysVisible(e.target.checked)}
                      />
                    }
                    label="Keep QR always visible (don't rotate it with other messages)"
                  />
                </>
              )}
            </Box>
          )}
          <Box sx={{ pt: 1 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || (walkinEnabled && !defaultEventId)}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

export default function OohScreensDrawer({ open, clinicLocation, onClose, onPreview }) {
  const clinicLocationId = clinicLocation?.id
  const [loading, setLoading] = useState(false)
  const [screens, setScreens] = useState([])
  const [eventsByMentor, setEventsByMentor] = useState({})

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!open || !clinicLocationId) return
      setLoading(true)
      try {
        const [screensResp, landingResp] = await Promise.all([
          getSupportOohScreens(clinicLocationId),
          getSPDataWithParams('/api/public/clinic-landing-page', { clinicId: clinicLocationId }),
        ])
        if (cancelled) return
        if (screensResp?.status === 200 && isCommonResponseSuccessfulV2(screensResp)) {
          setScreens(getDataMapFromCommonResponse(screensResp)?.screens || [])
        } else {
          setScreens([])
        }
        // Map mentorId -> offlineEvents from the public landing page.
        const practitioners =
          landingResp?.data?.data?.practitioners || landingResp?.data?.practitioners || []
        const map = {}
        for (const p of practitioners) {
          if (p?.id) map[p.id] = Array.isArray(p.offlineEvents) ? p.offlineEvents : []
        }
        setEventsByMentor(map)
      } catch (e) {
        console.error('load ooh screens failed', e)
        if (!cancelled) setScreens([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, clinicLocationId])

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 640, maxWidth: '95vw', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, #1a2332 0%, #243044 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              OOH / Token Screens
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {clinicLocation?.title || clinicLocation?.name}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : screens.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>
              No practitioners found at this location.
            </Typography>
          ) : (
            screens.map((screen) => (
              <OohScreenRow
                key={screen.mentorId}
                screen={screen}
                clinicLocationId={clinicLocationId}
                offlineEvents={eventsByMentor[screen.mentorId] || []}
                onPreview={onPreview}
              />
            ))
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
